import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  priceEngine,
  type CategoryPricingConfig,
  type FinancialLogEntry,
  type GlobalPricingConfig,
  type PriceHistoryEntry,
  type PriceProfile,
  type PriceSummary,
} from '@/services/price-engine'
import { zustandStorage } from '@/services/storage'

interface PriceStoreState {
  ready: boolean
  activeProfileId: string
  profiles: PriceProfile[]
  global: GlobalPricingConfig
  categories: CategoryPricingConfig[]
  history: PriceHistoryEntry[]
  logs: FinancialLogEntry[]
  simulatorProfitPercent: number
  simulatorDiscountPercent: number
  lastSummary: PriceSummary | null

  hydrate: () => void
  setActiveProfile: (id: string) => void
  updateGlobal: (patch: Partial<GlobalPricingConfig>) => void
  updateProfile: (id: string, patch: Partial<PriceProfile>) => void
  updateCategory: (
    category: string,
    patch: Partial<Omit<CategoryPricingConfig, 'category'>>,
  ) => void
  setSimulatorProfit: (value: number) => void
  setSimulatorDiscount: (value: number) => void
  setLastSummary: (summary: PriceSummary | null) => void
  refreshAudit: () => void
}

export const usePriceStore = create<PriceStoreState>()(
  persist(
    (set, get) => ({
      ready: false,
      activeProfileId: '',
      profiles: [],
      global: priceEngine.getGlobalConfig(),
      categories: [],
      history: [],
      logs: [],
      simulatorProfitPercent: 20,
      simulatorDiscountPercent: 0,
      lastSummary: null,

      hydrate: () => {
        priceEngine.ensureSeed()
        const profiles = priceEngine.listProfiles()
        const active = priceEngine.getActiveProfile()
        set({
          ready: true,
          profiles,
          activeProfileId: active.id,
          global: priceEngine.getGlobalConfig(),
          categories: priceEngine.listCategoryConfigs(),
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
          simulatorProfitPercent: active.profitPercent,
        })
      },

      setActiveProfile: (id) => {
        priceEngine.setActiveProfile(id)
        const active = priceEngine.getActiveProfile()
        set({
          activeProfileId: id,
          simulatorProfitPercent: active.profitPercent,
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
        })
      },

      updateGlobal: (patch) => {
        const global = priceEngine.updateGlobalConfig(patch)
        set({
          global,
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
        })
      },

      updateProfile: (id, patch) => {
        priceEngine.updateProfile(id, patch)
        set({
          profiles: priceEngine.listProfiles(),
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
        })
        if (get().activeProfileId === id && patch.profitPercent != null) {
          set({ simulatorProfitPercent: patch.profitPercent })
        }
      },

      updateCategory: (category, patch) => {
        priceEngine.updateCategoryConfig(category, patch)
        set({
          categories: priceEngine.listCategoryConfigs(),
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
        })
      },

      setSimulatorProfit: (value) => set({ simulatorProfitPercent: value }),
      setSimulatorDiscount: (value) => set({ simulatorDiscountPercent: value }),
      setLastSummary: (summary) => set({ lastSummary: summary }),

      refreshAudit: () =>
        set({
          history: priceEngine.listHistory(),
          logs: priceEngine.listLogs(),
        }),
    }),
    {
      name: 'masterhub-price-store',
      storage: zustandStorage,
      partialize: (state) => ({
        simulatorProfitPercent: state.simulatorProfitPercent,
        simulatorDiscountPercent: state.simulatorDiscountPercent,
        activeProfileId: state.activeProfileId,
      }),
    },
  ),
)
